import React, { useRef } from 'react';

import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import {
  DropdownIndicator,
  Option,
} from 'components/application/CollectionSelect';
import { DatePicker } from 'components/application/DatePickerField';

import { toSentence } from 'components/helpers/arrays';

const permittedPeriods = [
  'withinTheNext',
  'withinTheLast',
  'thisWeek',
  'nextWeek',
  'lastWeek',
  'thisMonth',
  'nextMonth',
  'lastMonth',
  'thisQuarter',
  'nextQuarter',
  'lastQuarter',
  'thisYear',
  'nextYear',
  'lastYear',
  'today',
  'tomorrow',
  'yesterday',
  'before',
  'after',
  'onExactDate',
  'customDateRange',
];

const permittedIntervals = ['days', 'weeks', 'months', 'years'];

const permittedPeriodsSet = new Set(permittedPeriods);

const integersRegex = /^\d+(\.\d{0,2})?/;

export default function DateFilter({
  afterProps = {},
  autoFocus,
  beforeProps = {},
  customDateRangeFromProps = {},
  customDateRangeToProps = {},
  datePeriod,
  id,
  label,
  name,
  nullPeriodOption,
  onChange,
  onExactDateProps = {},
  onFocus,
  onRemove,
  periodOptions,
  validationErrors = {},
}) {
  const selectPeriodRef = useRef();
  const selectIntervalRef = useRef();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handlePeriodChange = (selected) => {
    onChange('period', selected?.value || null);
  };

  const handleFrequencyChange = (event) => {
    const matchedValue = event.target.value.match(integersRegex);

    onChange(
      `${datePeriod.period}Frequency`,
      matchedValue ? matchedValue[0] : '',
    );
  };

  const handleIntervalChange = (selected) => {
    onChange(`${datePeriod.period}Interval`, selected?.value || '');
  };

  const handleDateRangeFromChange = (_, date) => {
    onChange('customDateRangeFrom', date);
  };

  const handleDateRangeToChange = (_, date) => {
    onChange('customDateRangeTo', date);
  };

  const formatIntervalOption = (option) => ({
    label: i18nTShared(`intervals.${option}`),
    value: option,
  });

  const formatPeriodOption = (option) => ({
    label: option ? i18nTShared(`periods.${option}`) : nullPeriodOption,
    value: option || '',
  });

  const isFrequencyIntervalPickerVisible = [
    'withinTheNext',
    'withinTheLast',
  ].includes(datePeriod.period);

  const isDatePickerVisible = ['before', 'after', 'onExactDate'].includes(
    datePeriod.period,
  );

  let dateProps;
  if (datePeriod.period === 'before') {
    dateProps = beforeProps;
  } else if (datePeriod.period === 'after') {
    dateProps = afterProps;
  } else if (datePeriod.period === 'onExactDate') {
    dateProps = onExactDateProps;
  }

  const isDatePickerFromToVisible = datePeriod.period === 'customDateRange';

  const selectablePeriodOptions = [
    ...(periodOptions ?
      periodOptions.filter((periodOption) =>
        permittedPeriodsSet.has(periodOption),
      )
    : permittedPeriods),
  ].concat(nullPeriodOption ? [null] : []);

  const validationErrorDetails = (() => {
    if (Object.keys(validationErrors).length === 0) return '';

    const details = Object.keys(validationErrors).map(
      (key) => validationErrors[key].error.detail,
    );

    return _.capitalize(toSentence(details));
  })();

  return (
    <div onFocus={onFocus}>
      <div className='tw-mb-4 tw-flex tw-items-center tw-justify-between'>
        <label className='tw-m-0 tw-font-medium'>{label}</label>
        <div
          className='tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-white tw-px-2 tw-text-grey-500 hover:tw-bg-red-025 hover:tw-text-red-500'
          onClick={onRemove}
        >
          <span className='tw-text-s tw-font-medium tw-tracking-wide'>
            {i18nTShared('removeFilter')}
          </span>
        </div>
      </div>
      {!!validationErrorDetails && (
        <div className='tw-mb-4 tw-rounded-lg tw-border-0 tw-bg-red-025 tw-p-3 tw-text-red-800'>
          {validationErrorDetails}
        </div>
      )}
      <div className='tw-mb-2'>
        <Select
          autoFocus={autoFocus}
          className='collection-select__select-container collection-select--transparent-option'
          classNamePrefix='collection-select'
          components={{ SingleValue, DropdownIndicator, Option }}
          controlShouldRenderValue={true}
          id={`${id}_period`}
          isSearchable={false}
          name={`${name}_period`}
          onChange={handlePeriodChange}
          openMenuOnFocus={true}
          options={selectablePeriodOptions.map((periodOption) =>
            formatPeriodOption(periodOption),
          )}
          ref={selectPeriodRef}
          styles={{
            menu: (base) => ({ ...base, maxHeight: 244 }),
            menuList: (base) => ({ ...base, maxHeight: 242 }),
          }}
          value={formatPeriodOption(datePeriod.period)}
        />
      </div>
      {isFrequencyIntervalPickerVisible && (
        <div className='tw-flex'>
          <div className='tw-mr-2 tw-w-20'>
            <input
              className={classNames(
                'field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
                validationErrors.frequency?.fieldHighlighted ?
                  'tw-border-red-600 focus-within:tw-border-red-600 hover:tw-border-red-600 hover:focus-within:tw-border-red-600'
                : 'tw-border-grey-300 focus-within:tw-border-blue-300 hover:tw-border-grey-400 hover:focus-within:tw-border-blue-300',
              )}
              id={`${id}_frequency`}
              name={`${name}_frequency`}
              onChange={handleFrequencyChange}
              type='text'
              value={datePeriod[`${datePeriod.period}Frequency`]}
            />
          </div>
          <div className='tw-w-full'>
            <Select
              autoFocus={false}
              className='collection-select__select-container collection-select--transparent-option'
              classNamePrefix='collection-select'
              components={{ SingleValue, DropdownIndicator, Option }}
              controlShouldRenderValue={true}
              id={`${id}_interval`}
              isSearchable={false}
              name={`${name}_interval`}
              onChange={handleIntervalChange}
              options={permittedIntervals.map((intervalOption) =>
                formatIntervalOption(intervalOption),
              )}
              ref={selectIntervalRef}
              value={formatIntervalOption(
                datePeriod[`${datePeriod.period}Interval`],
              )}
            />
          </div>
        </div>
      )}
      {isDatePickerVisible && (
        <div className='tw-w-36'>
          <DatePicker
            fieldError={validationErrors.date}
            id={`${id}_date`}
            latestDate={dateProps.latestDate}
            name={`${name}_date`}
            onChange={(_, date) => onChange(datePeriod.period, date)}
            value={datePeriod[datePeriod.period]}
          />
        </div>
      )}
      {isDatePickerFromToVisible && (
        <div className='tw-flex'>
          <DatePicker
            fieldError={validationErrors.customDateRangeFrom}
            id={`${id}_custom_date_range_from`}
            latestDate={customDateRangeFromProps.latestDate}
            name={`${name}_custom_date_range_from`}
            onChange={handleDateRangeFromChange}
            placement='bottom-start'
            value={datePeriod.customDateRangeFrom}
          />
          <div className='tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-font-medium'>
            to
          </div>
          <DatePicker
            fieldError={validationErrors.customDateRangeTo}
            id={`${id}_custom_date_range_to`}
            latestDate={customDateRangeToProps.latestDate}
            name={`${name}_custom_date_range_to`}
            onChange={handleDateRangeToChange}
            placement='bottom-end'
            value={datePeriod.customDateRangeTo}
          />
        </div>
      )}
    </div>
  );
}

DateFilter.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  datePeriod: PropTypes.object.isRequired,
  periodOptions: PropTypes.array,
  nullPeriodOption: PropTypes.string,
  validationErrors: PropTypes.object,
  beforeProps: PropTypes.shape({ latestDate: PropTypes.instanceOf(Date) }),
  afterProps: PropTypes.shape({ latestDate: PropTypes.instanceOf(Date) }),
  onExactDateProps: PropTypes.shape({ latestDate: PropTypes.instanceOf(Date) }),
  customDateRangeFromProps: PropTypes.shape({
    latestDate: PropTypes.instanceOf(Date),
  }),
  customDateRangeToProps: PropTypes.shape({
    latestDate: PropTypes.instanceOf(Date),
  }),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
};

function SingleValue({ ...props }) {
  return (
    <components.SingleValue {...props}>
      <div className='tw-relative -tw-top-[1px]'>{props.children}</div>
    </components.SingleValue>
  );
}
